import { FC, useEffect, useState } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';
// components
import { PriceDisplay } from 'dibs-buyer-price';
import { OriginalListPriceLazy } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedSidebarFavorites } from '../shared/PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PdpSharedShippingBadgeLazy } from '../PdpSharedShippingBadge/PdpSharedShippingBadgeLazy';
import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { PdpSharedCondensedSkuAttributes } from '../PdpSharedCondensedSkuAttributes/PdpSharedCondensedSkuAttributes';
import { PdpSharedCondensedSidebarButtons } from '../PdpSharedCondensedSidebarButtons/PdpSharedCondensedSidebarButtons';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';
import { PdpSharedCondensedPriceDisplay } from '../PdpSharedCondensedAuctionPriceDisplay/PdpSharedCondensedAuctionPriceDisplay';

// Helpers
import { getSelectedSku } from '../helpers/getSelectedSku';
import { useSharedAuctionContext } from '../PdpSharedAuctionContext/PdpSharedAuctionContext';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// styles
import styles from './PdpRespCondensedListedContent.scss';

// types
import type { PdpRespCondensedListedContent_viewer$key } from './__generated__/PdpRespCondensedListedContent_viewer.graphql';
import type { PdpRespCondensedListedContent_user$key } from './__generated__/PdpRespCondensedListedContent_user.graphql';
import type { PdpRespCondensedListedContent_item$key } from './__generated__/PdpRespCondensedListedContent_item.graphql';

type Props = {
    currency?: string;
    selectedSkuId?: string;
    viewer: PdpRespCondensedListedContent_viewer$key;
    user?: PdpRespCondensedListedContent_user$key;
    item: PdpRespCondensedListedContent_item$key;
};

export const PdpRespCondensedListedContent: FC<Props> = ({
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    currency,
    selectedSkuId,
}) => {
    const viewer = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_viewer on Viewer {
                showHeartCount: featureFlag(feature: "pdpDisplayHeartCount")
                ...PdpRespHeroCarousel_viewer
                ...PdpSharedShippingBadge_viewer
                ...PdpSharedPromoCode_viewer
                ...PdpSharedCondensedSidebarButtons_viewer
            }
        `,
        viewerRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_user on User {
                ...PdpRespHeroCarousel_user
                ...PdpSharedCondensedSidebarButtons_user
                ...PdpSharedPromoCode_user
            }
        `,
        userRef || null
    );
    const item = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_item on Item {
                firstPhotoWebPath(size: small)
                isAuctionEnabled
                title
                displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpSharedCondensedSidebarButtons_displayInfo
                }
                seller {
                    isAccessible
                }
                skus {
                    serviceId
                    ...PdpSharedBlurbLeadTime_sku @skip(if: $isCondensedPdpRedesignVariant)
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        )
                }
                ...PriceDisplay_item
                    @include(if: $isClient)
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    )
                ...PdpSharedCondensedAuctionPriceDisplay @include(if: $fetchAuction)
                ...PdpSharedCondensedSkuAttributes_item @include(if: $isMultiSku)
                ...PdpSharedShippingBadge_item
                ...PdpSharedFramingStatus_item @include(if: $isClient)
                ...PdpRespHeroCarousel_item
                ...PdpRespTitle_item
                ...PdpSharedCondensedSidebarButtons_item
                ...PdpSharedBlurbLeadTime_item @skip(if: $isCondensedPdpRedesignVariant)
                ...PdpSharedSidebarFavorites_item
                ...PdpSharedAuctionCountdownLazy_item @include(if: $fetchAuction)
                ...OriginalListPriceLazy_item @include(if: $fetchAuction)
            }
        `,
        itemRef
    );

    const relayEnvironment = useRelayEnvironment();
    const { isAuctionSiderailRedesignVariant } = useServerVarsContext();
    const [hasFreeShipping, setHasFreeShipping] = useState(false);
    const relayVariables = useAppSelector(state => state.relayVariables.variables);
    const { userId, itemId, isClient, isMultiSku, fetchAuction, isCondensedPdpRedesignVariant } =
        relayVariables;
    const displayInfo = item?.displayInfo || null;
    const isSellerAccessible = item?.seller?.isAccessible;
    const selectedSku = getSelectedSku(item, selectedSkuId || '');
    const isAuctionEnabled = item?.isAuctionEnabled;
    const { setShouldDisplayCountdown } = useSharedAuctionContext();

    useEffect(() => {
        if (isAuctionEnabled) {
            setShouldDisplayCountdown(true);
        }
    }, [setShouldDisplayCountdown, isAuctionEnabled]);

    return (
        <div
            className={classnames(styles.contentWrapper, {
                [styles.withBottomPadding]: !isSellerAccessible || isCondensedPdpRedesignVariant,
                [styles.isCondensedPdpRedesignVariant]: isCondensedPdpRedesignVariant,
            })}
        >
            <div
                data-tn="condensed-image-carousel"
                className={classnames(styles.contentColumn, {
                    [styles.isSidebarColumn]: isCondensedPdpRedesignVariant,
                })}
            >
                {/* Image Carousel */}
                <PdpRespHeroCarousel
                    itemTitle={item.title || ''}
                    itemImage={item.firstPhotoWebPath || ''}
                    item={item}
                    user={user}
                    environment={relayEnvironment}
                    relayVariables={relayVariables}
                    viewer={viewer}
                    disableZoomModal={false}
                    showCounter
                    isCondensedCarousel
                    selectedSkuId={selectedSkuId}
                    hideBubbles
                    verticalThumbsPerPage={4}
                    currency={currency}
                    hideThumbs={isCondensedPdpRedesignVariant}
                />
            </div>

            {/* Sidebar */}
            <div
                className={classnames(styles.contentColumn, {
                    [styles.isSidebarColumn]: isCondensedPdpRedesignVariant,
                })}
            >
                {/* Auction timer */}
                {fetchAuction && !isAuctionSiderailRedesignVariant && (
                    <PdpSharedAuctionCountdownLazy
                        isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}
                        item={item}
                    />
                )}
                <div className={styles.titleFavoritesWrapper}>
                    {/* Title */}
                    <PdpRespTitle item={item} />
                    {/* Favorite / Folder */}
                    <PdpSharedSidebarFavorites
                        environment={relayEnvironment}
                        heartIconWrapperClass={styles.heartIconWrapper}
                        itemImage={item.firstPhotoWebPath || ''}
                        item={item}
                        showHeartCount={viewer.showHeartCount}
                        isClient={!!isClient}
                        userId={userId}
                        wrapperClass={styles.favoritesWrapper}
                        fetchFolder={!isCondensedPdpRedesignVariant}
                    />
                </div>
                {fetchAuction && (
                    <OriginalListPriceLazy
                        item={item}
                        currency={currency || ''}
                        className={styles.originalListPrice}
                    />
                )}
                {/* Shipping Badge */}
                {!isCondensedPdpRedesignVariant && isClient && (
                    <div
                        className={classnames(styles.shippingBadgeContainer, {
                            [styles.noMargin]: !hasFreeShipping,
                        })}
                    >
                        <PdpSharedShippingBadgeLazy
                            item={item}
                            viewer={viewer}
                            setHasFreeShipping={setHasFreeShipping}
                        />
                    </div>
                )}
                {/* Price */}
                <div className={styles.priceWithAccessoryInlineContainer}>
                    {fetchAuction && (
                        <PdpSharedCondensedPriceDisplay
                            currency={currency || ''}
                            isMultiSku={!!isMultiSku}
                            item={item}
                            selectedSkuId={selectedSkuId || ''}
                        />
                    )}

                    {!isAuctionEnabled && isClient && (
                        <PriceDisplay
                            currency={currency}
                            item={item}
                            useSvgIcon
                            sku={isMultiSku ? selectedSku : null}
                            appendElementToPrice={<PdpSharedFramingStatusLazy item={item} />}
                            displayVatLabel
                        />
                    )}
                </div>
                {/* Promo Code */}
                <PdpSharedPromoCode
                    displayInfo={displayInfo}
                    itemId={itemId}
                    user={user}
                    userId={userId}
                    viewer={viewer}
                    currency={currency}
                    excludeShippingPromos
                />

                {isMultiSku && (
                    <PdpSharedCondensedSkuAttributes item={item} selectedSkuId={selectedSkuId} />
                )}

                {/* Item Ctas */}
                <PdpSharedCondensedSidebarButtons
                    relayVariables={{
                        userId,
                        itemId,
                        fetchAuction,
                        isCondensedPdpRedesignVariant,
                    }}
                    user={user}
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    displayInfo={displayInfo}
                    selectedSkuId={selectedSkuId}
                />

                {!isCondensedPdpRedesignVariant && (
                    <PdpSharedBlurbLeadTime sku={selectedSku} item={item} />
                )}
            </div>
        </div>
    );
};
